import type { SxProps, Theme } from "@mui/material/styles";

export const VOICE_INPUT_HEIGHT = 40;

export const TELEGRAM_BLUE = "#3390EC";
export const TELEGRAM_BLUE_SOFT = "#5AC8FA";
export const TELEGRAM_BAR_BG = "rgba(24, 24, 28, 0.92)";

export const recordingOverlayStyle = (): SxProps<Theme> => ({
  position: "relative",
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  height: VOICE_INPUT_HEIGHT,
  minHeight: VOICE_INPUT_HEIGHT,
  maxHeight: VOICE_INPUT_HEIGHT,
  userSelect: "none",
  touchAction: "none",
});

export const recordingBarStyle = (
  isCancelling: boolean,
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  width: "100%",
  height: VOICE_INPUT_HEIGHT,
  minHeight: VOICE_INPUT_HEIGHT,
  maxHeight: VOICE_INPUT_HEIGHT,
  pl: 1.5,
  // Room for the enlarged hold-to-talk mic.
  pr: "76px",
  borderRadius: "999px",
  backgroundColor: isCancelling ? "rgba(254, 226, 226, 1)" : "#FFFFFF",
  color: isCancelling ? "#B91C1C" : "inherit",
  boxShadow: "none",
  transition: "background-color 0.15s ease",
  overflow: "visible",
  boxSizing: "border-box",
});

export const lockedBarStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: VOICE_INPUT_HEIGHT,
  minHeight: VOICE_INPUT_HEIGHT,
  maxHeight: VOICE_INPUT_HEIGHT,
  pl: 1.25,
  pr: "52px",
  borderRadius: "999px",
  backgroundColor: "#FFFFFF",
  color: "#1C1C1F",
  gap: 1,
  boxSizing: "border-box",
};

export const lockedTimerGroupStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexShrink: 0,
};

export const lockedCancelTextStyle: SxProps<Theme> = {
  flex: 1,
  textAlign: "center",
  fontSize: "14px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  color: TELEGRAM_BLUE,
  cursor: "pointer",
  userSelect: "none",
  py: 0,
  border: "none",
  background: "transparent",
  fontFamily: "inherit",
  lineHeight: 1,
  "&:active": {
    opacity: 0.65,
  },
};

export const pausedRowStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: VOICE_INPUT_HEIGHT,
  minHeight: VOICE_INPUT_HEIGHT,
  maxHeight: VOICE_INPUT_HEIGHT,
  gap: 0,
};

export const trashButtonStyle = (isDeleting: boolean): SxProps<Theme> => ({
  width: 32,
  height: 32,
  flexShrink: 0,
  color: "#8B7BA8",
  backgroundColor: "transparent",
  p: 0.5,
  ml: 0.25,
  pointerEvents: isDeleting ? "none" : "auto",
  ...(isDeleting
    ? {
        animation: "trashDeleteAnim 0.48s ease forwards",
        "@keyframes trashDeleteAnim": {
          "0%": {
            color: "#8B7BA8",
            opacity: 1,
            transform: "scale(1)",
          },
          "28%": {
            color: "#FF3B30",
            opacity: 1,
            transform: "scale(1.08)",
          },
          "100%": {
            color: "#FF3B30",
            opacity: 0,
            transform: "scale(0.7)",
          },
        },
      }
    : {
        transition: "color 0.2s ease, background-color 0.2s ease",
      }),
  "&:hover": {
    backgroundColor: "rgba(139, 123, 168, 0.12)",
    color: isDeleting ? "#FF3B30" : "#6D5A8F",
  },
});

export const pausedPreviewBarStyle: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  position: "relative",
  height: VOICE_INPUT_HEIGHT,
  minHeight: VOICE_INPUT_HEIGHT,
  maxHeight: VOICE_INPUT_HEIGHT,
  borderRadius: "999px",
  backgroundColor: "#FFFFFF",
  overflow: "hidden",
  pl: 0.5,
  pr: "52px",
  boxSizing: "border-box",
};

export const pausedWaveformWrapStyle: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  opacity: 0.9,
  px: 1.25,
  display: "flex",
  alignItems: "center",
};

export const pausedPlayPillStyle: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.35,
  borderRadius: "999px",
  backgroundColor: TELEGRAM_BLUE,
  color: "#FFFFFF",
  border: "none",
  cursor: "pointer",
  minHeight: 26,
  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
  "&:hover": {
    backgroundColor: "#2B7FD4",
  },
};

export const pausedPlayTimeStyle: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  lineHeight: 1,
  color: "#FFFFFF",
};

export const recordingDotStyle: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#FF3B30",
  flexShrink: 0,
  animation: "telegramRecPulse 1s ease-in-out infinite",
  "@keyframes telegramRecPulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 },
  },
};

export const cancelHintStyle = (
  opacity: number,
  isCancelling: boolean,
): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  pr: "12px",
  fontSize: "12px",
  fontWeight: 600,
  // White recording bar in both themes — keep dark readable text.
  color: isCancelling ? "#B91C1C" : "rgba(60, 48, 80, 0.72)",
  opacity,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: "opacity 0.12s ease, color 0.12s ease",
  animation: isCancelling
    ? "none"
    : "slideToCancelNudge 2.5s ease-in-out infinite",
  "@keyframes slideToCancelNudge": {
    "0%, 100%": { transform: "translateX(6px)" },
    "50%": { transform: "translateX(-10px)" },
  },
});

export const lockPillStyle = (
  lockProgress: number,
  isDarkMode: boolean,
): SxProps<Theme> => ({
  position: "absolute",
  left: "50%",
  bottom: `calc(100% + ${10 + lockProgress * 24}px)`,
  transform: `translateX(-50%) scale(${0.92 + lockProgress * 0.08})`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  width: 40,
  height: 72,
  borderRadius: "20px",
  backgroundColor: isDarkMode ? "#4A4A55" : TELEGRAM_BAR_BG,
  border: isDarkMode
    ? "1px solid rgba(255,255,255,0.22)"
    : "1px solid transparent",
  color: lockProgress >= 1 ? "#FFFFFF" : "rgba(255,255,255,0.92)",
  boxShadow: isDarkMode
    ? "0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)"
    : "0 8px 24px rgba(0,0,0,0.28)",
  opacity: isDarkMode
    ? 0.88 + lockProgress * 0.12
    : 0.6 + lockProgress * 0.4,
  transition: "opacity 0.12s ease, transform 0.05s linear, bottom 0.05s linear",
  pointerEvents: "none",
  zIndex: 20,
});

export const sendTopActionStyle = (): SxProps<Theme> => ({
  position: "absolute",
  left: "50%",
  bottom: "calc(100% + 10px)",
  transform: "translateX(-50%)",
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Match trash / menu accent on the white recording bar.
  backgroundColor: "#8B7BA8",
  color: "#FFFFFF",
  boxShadow: "0 4px 14px rgba(139, 123, 168, 0.35)",
  zIndex: 30,
  border: "none",
  cursor: "pointer",
  p: 0,
  "&:hover": {
    backgroundColor: "#6D5A8F",
    opacity: 1,
  },
  "&:active": {
    backgroundColor: "#5A4A78",
  },
});

export const lockedSendButtonSx = (isSmallScreen: boolean): SxProps<Theme> => ({
  background: TELEGRAM_BLUE,
  color: "#ffffff",
  borderRadius: "50%",
  width: "100%",
  height: "100%",
  p: 0,
  animation: "telegramSendGlow 2.4s ease-in-out infinite",
  "@keyframes telegramSendGlow": {
    "0%, 100%": {
      boxShadow: `
        0 0 0 4px rgba(51, 144, 236, 0.14),
        0 0 0 8px rgba(51, 144, 236, 0.06)
      `,
    },
    "50%": {
      boxShadow: `
        0 0 0 6px rgba(51, 144, 236, 0.28),
        0 0 0 12px rgba(51, 144, 236, 0.1)
      `,
    },
  },
  "& svg": {
    display: "block",
    width: isSmallScreen ? 20 : 24,
    height: isSmallScreen ? 20 : 24,
  },
  "&:hover": {
    background: "#2B7FD4",
  },
});
